import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CertificatesFilterParams,
  CertificateType,
  CertificatesListResponse,
  CreateCertificateParams,
  UpdateCertificateParams
} from '@/types/certificate'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import {
  fetchCertificates,
  fetchCertificate,
  createCertificate,
  updateCertificate,
  deleteCertificate,
  bulkDeleteCertificates,
  sortCertificate
} from '@/lib/api'

type CertificateStateType = {
  data: CertificateType | null
  isLoading: boolean
  error: string | null
}

type CertificatesListStateType = {
  data: CertificatesListResponse | null
  isLoading: boolean
  error: string | null
}

type CertificatesStoreState = {
  certificate: CertificateStateType
  certificatesList: CertificatesListStateType
  filters: CertificatesFilterParams
  selectedCertificateIds: string[]
  setFilters: (filters: Partial<CertificatesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchCertificates: (params?: CertificatesFilterParams) => Promise<CertificatesListResponse>
  resetCertificatesList: () => void

  // Sort certificate
  sortCertificate: (id: string, type: SortOrderEnum) => Promise<CertificateType>

  // Blog Post operations
  getCertificate: (id: string) => Promise<CertificateType>
  createCertificate: (params: CreateCertificateParams) => Promise<CertificateType>
  updateCertificate: (id: string, params: UpdateCertificateParams) => Promise<CertificateType>
  deleteCertificate: (id: string) => Promise<void>
  bulkDeleteCertificates: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectCertificate: (id: string) => void
  deselectCertificate: (id: string) => void
  toggleCertificateSelection: (id: string) => void
  selectAllCertificates: (certificates: CertificateType[]) => void
  deselectAllCertificates: () => void
  isCertificateSelected: (id: string) => boolean
  resetCertificate: () => void
}

const defaultFilters: CertificatesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const initialCertificateState: CertificateStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialCertificatesListState: CertificatesListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useCertificatesStore = create<CertificatesStoreState>()(
  devtools((set, get) => ({
    certificate: initialCertificateState,
    certificatesList: initialCertificatesListState,
    selectedCertificateIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof CertificatesFilterParams]
          } else {
            updatedFilters[key as keyof CertificatesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: CertificatesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as CertificatesFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof CertificatesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getCertificate: async id => {
      set(state => ({
        certificate: {
          ...state.certificate,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchCertificate(id)

        set(state => ({
          certificate: {
            ...state.certificate,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          certificate: {
            ...state.certificate,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createCertificate: async params => {
      set(state => ({
        certificate: {
          ...state.certificate,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createCertificate(params)

        set(state => ({
          certificate: {
            ...state.certificate,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          certificate: {
            ...state.certificate,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateCertificate: async (id, params) => {
      set(state => ({
        certificate: {
          ...state.certificate,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateCertificate(id, params)

        set(state => ({
          certificate: {
            ...state.certificate,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          certificate: {
            ...state.certificate,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteCertificate: async id => {
      set(state => ({
        certificate: {
          ...state.certificate,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteCertificate(id)

        set(state => ({
          certificate: {
            ...state.certificate,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          certificate: {
            ...state.certificate,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetCertificate: () => {
      set(state => ({
        certificate: {
          ...state.certificate,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectCertificate: id => {
      set(state => {
        if (state.selectedCertificateIds.includes(id)) {
          return state
        }
        return { selectedCertificateIds: [...state.selectedCertificateIds, id] }
      })
    },

    deselectCertificate: id => {
      set(state => ({
        selectedCertificateIds: state.selectedCertificateIds.filter(certificateId => certificateId !== id)
      }))
    },

    toggleCertificateSelection: id => {
      set(state => {
        if (state.selectedCertificateIds.includes(id)) {
          return {
            selectedCertificateIds: state.selectedCertificateIds.filter(certificateId => certificateId !== id)
          }
        }
        return { selectedCertificateIds: [...state.selectedCertificateIds, id] }
      })
    },

    selectAllCertificates: certificates => {
      set(() => ({
        selectedCertificateIds: certificates.map(certificate => certificate.id)
      }))
    },

    deselectAllCertificates: () => {
      set(() => ({ selectedCertificateIds: [] }))
    },

    isCertificateSelected: id => {
      return get().selectedCertificateIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteCertificates: async ids => {
      set(state => ({
        certificate: {
          ...state.certificate,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteCertificates({ ids })

        set(state => ({
          certificate: {
            ...state.certificate,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedCertificateIds: state.selectedCertificateIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          certificate: {
            ...state.certificate,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchCertificates: async (params?: CertificatesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        certificatesList: {
          ...state.certificatesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchCertificates(filtersToUse)

        set(state => ({
          certificatesList: {
            ...state.certificatesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          certificatesList: {
            ...state.certificatesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetCertificatesList: () => {
      set(state => ({
        certificatesList: {
          ...state.certificatesList,
          data: null,
          error: null
        }
      }))
    },

    sortCertificate: async (id, type) => {
      set(() => ({
        certificate: {
          ...get().certificate,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortCertificate(id, type)

        set(() => ({
          certificate: {
            ...get().certificate,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          certificate: {
            ...initialCertificateState,
            error: getErrorMessage(error)
          }
        }))
        throw error
      }
    }
  }))
)

export default useCertificatesStore
